import { yupResolver } from '@hookform/resolvers/yup';
import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { MouseEventHandler, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { InferType, object, string } from 'yup';

import { postCurrencyLockApi, postCurrencyUnlockApi } from '@flipster/apis/v1/currency';
import { earnSummaryBunja, getEarnSummaryOfCurrency } from '@flipster/features/src/shared/earn/state';
import { useOverlayItem } from '@flipster/overlay';
import { Button } from '@flipster/react/design-system';
import { Modal } from '@flipster/react/design-system-legacy/modal';
import { createSegmentedControl } from '@flipster/react/design-system/segmented-control';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { walletsBunja } from '@flipster/socket/r230522/wallet';
import { css } from '@flipster/styled-system/css';
import { flex, stack } from '@flipster/styled-system/patterns';
import { DefaultNumberInputMaxLength, FormItem } from '@flipster/ui';
import { PusdtCurrency, PusdtDisplayName, UsdtCurrency, UsdtPrecision } from '@flipster/utils/constants/currency';
import { getErrorMessage } from '@flipster/utils/error';
import Num, { BigNumber, inputFormatter, numberFormatter } from '@flipster/utils/num';
import segment from '@flipster/utils/segment';
import { UsdtPremiumEvent } from '@flipster/utils/segment/events/usdt-premium';

import PriceRateButtons from './price-rate-buttons';
import SendPriceInput from './send-price-input';

type ModalSchema = InferType<typeof modalValidator>;
const modalValidator = object({
  amount: string().required(),
});

const SegmentedControl = createSegmentedControl<'add' | 'reduce'>();

const ManagePusdtModal = () => {
  const { onClose } = useOverlayItem();
  const [tab, setTab] = useState<'add' | 'reduce'>('add');
  const { earnSummaryAtom, refetch: refetchEarn } = useBunja(earnSummaryBunja);
  const { walletsAtom } = useBunja(walletsBunja);

  const wallets = useAtomValue(walletsAtom);
  const earnSummary = useAtomValue(earnSummaryAtom);

  const { pendingReductions } = getEarnSummaryOfCurrency(PusdtCurrency, earnSummary);
  const { appliedAprPct: usdtAppliedAprPct } = getEarnSummaryOfCurrency(UsdtCurrency, earnSummary);
  const pendingReductionsSum =
    pendingReductions.length > 0 ? BigNumber.sum(...pendingReductions.map((r) => Num(r.amount))) : Num('0');

  const assetAmount =
    tab === 'add'
      ? wallets?.[UsdtCurrency]?.walletBalanceWithdrawable
      : Num(wallets?.[PusdtCurrency]?.walletBalanceExclWithdrawalPending ?? '0').minus(pendingReductionsSum);

  const methods = useForm<ModalSchema>({
    mode: 'onTouched',
    resolver: yupResolver(modalValidator),
    defaultValues: {
      amount: '0',
    },
  });

  const {
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    reset,
    watch,
  } = methods;

  const isDisabled = !isValid || Num(watch('amount')).isZero();

  const onSubmit = async (data: ModalSchema) => {
    try {
      if (tab === 'add') {
        await postCurrencyLockApi(UsdtCurrency, { amount: data.amount });
      } else {
        await postCurrencyUnlockApi(UsdtCurrency, { amount: data.amount });
      }
      onClose();
      reset();
      refetchEarn();
      const formattedAmount = numberFormatter(data.amount, { precision: UsdtPrecision, toFixed: true });
      snackbar.success({
        message:
          tab === 'add'
            ? `${formattedAmount} ${UsdtCurrency} has been successfully added to ${PusdtDisplayName}.`
            : `${formattedAmount} ${UsdtCurrency} has been successfully reduced from ${PusdtDisplayName}.`,
      });
      segment.track(UsdtPremiumEvent.UsdtPremiumConfirmClick, {
        usdt: Num(data.amount).toNumber(),
        manage_type: tab === 'add' ? 'Add' : 'Reduce',
      });
    } catch (error) {
      snackbar.error({ message: getErrorMessage(error) });
    }
  };

  const onClickPercent: MouseEventHandler<HTMLButtonElement> = (e) => {
    const { value } = e.currentTarget;
    if (!assetAmount) return;
    const calculatedAmount = Num(assetAmount)
      .dividedBy(100)
      .multipliedBy(value)
      .toFixed(UsdtPrecision, BigNumber.ROUND_DOWN);
    const formattedAmount = inputFormatter(calculatedAmount, {
      withComma: true,
      maxLength: DefaultNumberInputMaxLength,
    });
    const withoutComma = inputFormatter(formattedAmount, { withComma: false });
    setValue('amount', withoutComma, { shouldDirty: true, shouldValidate: true });
  };

  return (
    <Modal size='small'>
      <Modal.Close onClose={onClose} />
      <Modal.Header>
        <Modal.Title label={`Manage ${PusdtDisplayName}`} />
      </Modal.Header>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} className={css(stack.raw({ flex: 1 }))}>
          <Modal.Content css={tab === 'add' && { pb: 0 }}>
            <SegmentedControl
              value={tab}
              defaultValue='add'
              size='medium'
              css={{ width: '100%' }}
              onChange={(value) => {
                setTab(value);
                reset(undefined, { keepDefaultValues: true });
              }}
            >
              <SegmentedControl.Item value='add'>
                <div className={css({ textStyle: 'Text/S/Strong' })}>Add</div>
              </SegmentedControl.Item>
              <SegmentedControl.Item value='reduce'>
                <div className={css({ textStyle: 'Text/S/Strong' })}>Reduce</div>
              </SegmentedControl.Item>
            </SegmentedControl>
            <div
              className={flex({
                align: 'start',
                justify: 'center',
                textStyle: 'Title/M',
                color: 'content.default.level-4',
                p: 24,
              })}
            >
              <FormItem name='amount' error={errors.amount?.message} className={css({ textAlign: 'center' })}>
                <SendPriceInput name='amount' precision={UsdtPrecision} currency={UsdtCurrency} />
              </FormItem>
            </div>
            <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-2', textAlign: 'center' })}>
              {tab === 'add' ? 'Add USDT to' : 'Reduce USDT from'} {PusdtDisplayName}
            </p>

            <PriceRateButtons
              items={[
                { label: '25%', value: '25' },
                { label: '50%', value: '50' },
                { label: '75%', value: '75' },
                { label: '100%', value: '100' },
              ]}
              onClick={onClickPercent}
              className={css({ mt: 24 })}
            />
            <ul
              className={css({
                pt: 24,
                pl: 16,
                textStyle: 'Text/L/Regular',
                color: 'content.default.level-3',
                '& > li': { listStyle: 'disc' },
                visibility: tab === 'reduce' ? 'visible' : 'hidden',
              })}
            >
              <li>Takes 24 hours to process conversion to USDT</li>
              <li>Reducing will convert that amount to USDT and reduce your APR to {usdtAppliedAprPct.toFixed(2)}%.</li>
            </ul>
          </Modal.Content>
          <Modal.Footer css={tab === 'add' && { pt: 10 }}>
            <Button
              type='submit'
              hierarchy='primary'
              color='accent'
              size='large'
              label='Confirm'
              disabled={isDisabled}
            />
          </Modal.Footer>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default ManagePusdtModal;
